import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardsApi } from '../api/dashboards';
import PremiumLayout from '../components/PremiumLayout';
import {
  DocumentTextIcon,
  PresentationChartBarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';

const StrategyCreativeDashboard: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'strategy-creative'],
    queryFn: dashboardsApi.getStrategyCreative,
  });

  if (isLoading) {
    return (
      <PremiumLayout title="Strategy / Creative Dashboard">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  if (error) {
    return (
      <PremiumLayout title="Strategy / Creative Dashboard">
        <div className="text-center py-12">
          <p className="text-red-600">Error loading dashboard data</p>
        </div>
      </PremiumLayout>
    );
  }

  const dashboardData = data || {} as any;
  const sowVersions = dashboardData.sowVersions || [];
  const deckStats = dashboardData.deckStats || {};
  const commercialStats = dashboardData.commercialStats || {};
  const sowCycleStats = dashboardData.sowCycleStats || [];

  return (
    <PremiumLayout
      title="Strategy / Creative Dashboard"
      subtitle="SOW governance, deck intelligence, and iteration tracking"
    >
      <div className="space-y-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="premium-card p-6 bg-gradient-to-br from-blue-50 to-cyan-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                <DocumentTextIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Total SOW Versions</p>
            <p className="text-3xl font-extrabold text-gray-900">{sowVersions.length}</p>
          </div>

          <div className="premium-card p-6 bg-gradient-to-br from-purple-50 to-pink-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-500 rounded-xl shadow-lg">
                <PresentationChartBarIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Total Decks</p>
            <p className="text-3xl font-extrabold text-gray-900">{deckStats.total_decks || 0}</p>
          </div>

          <div className="premium-card p-6 bg-gradient-to-br from-green-50 to-emerald-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-500 rounded-xl shadow-lg">
                <ChartBarIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Total Commercials</p>
            <p className="text-3xl font-extrabold text-gray-900">{commercialStats.total_commercials || 0}</p>
          </div>

          <div className="premium-card p-6 bg-gradient-to-br from-orange-50 to-red-50">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-500 rounded-xl shadow-lg">
                <ArrowTrendingUpIcon className="h-7 w-7 text-white" />
              </div>
            </div>
            <p className="text-sm font-semibold text-gray-600 mb-2 uppercase tracking-wide">Leads with Decks</p>
            <p className="text-3xl font-extrabold text-gray-900">{deckStats.leads_with_decks || 0}</p>
          </div>
        </div>

        {/* SOW Iteration Cycle */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">SOW Iteration Cycle</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lead</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Iterations</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">First Version</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Latest Version</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cycle Days</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sowCycleStats.slice(0, 10).map((stat: any) => (
                  <tr key={stat.lead_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Lead #{stat.lead_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.iteration_count}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stat.first_version ? new Date(stat.first_version).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {stat.latest_version ? new Date(stat.latest_version).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {stat.cycle_days ? Math.round(stat.cycle_days) : '—'} days
                    </td>
                  </tr>
                ))}
                {sowCycleStats.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      No SOW iteration data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent SOW Versions */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Recent SOW Versions</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Lead</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Stage</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">File</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created By</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sowVersions.slice(0, 20).map((version: any) => (
                  <tr key={version.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {version.lead_name || `Lead #${version.lead_id}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {version.stage || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">v{version.version_number}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{version.file_name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{version.created_by_name || '—'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {version.created_at ? new Date(version.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
                {sowVersions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      No SOW versions available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </PremiumLayout>
  );
};

export default StrategyCreativeDashboard;
